import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';

export interface ChatMessage {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp: number;
}

export interface MoneyCoachResponse {
    response: string;
    success: boolean;
    error?: string;
    remainingQueries?: number;
}

/**
 * Send a message to the Money Coach AI
 */
export async function sendMoneyCoachMessage(query: string): Promise<MoneyCoachResponse> {
    const processQuery = httpsCallable<{ query: string }, MoneyCoachResponse>(
        functions,
        'processMoneyCoachQuery'
    );

    try {
        const result = await processQuery({ query });

        if (!result.data.success) {
            console.error('[MoneyCoach] API returned error:', result.data.error);
            throw new Error(result.data.error || 'Failed to get response');
        }

        return result.data;
    } catch (error: any) {
        console.error('[MoneyCoach] Error details:', {
            message: error?.message,
            code: error?.code,
            details: error?.details,
            fullError: error
        });
        throw error;
    }
}

/**
 * Generate a unique message ID
 */
export function generateMessageId(): string {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Get conversation history from server
 */
export async function getConversationHistory(): Promise<ChatMessage[]> {
    const getHistory = httpsCallable<void, { success: boolean; messages: Array<{ role: 'user' | 'assistant'; content: string; timestamp: number }>; error?: string }>(
        functions,
        'getMoneyCoachConversation'
    );

    try {
        const result = await getHistory();
        if (!result.data.success) {
            console.error('[MoneyCoach] Failed to get history:', result.data.error);
            return [];
        }

        // Convert to ChatMessage format with generated IDs
        return result.data.messages.map(m => ({
            id: generateMessageId(),
            role: m.role,
            content: m.content,
            timestamp: m.timestamp
        }));
    } catch (error) {
        console.error('[MoneyCoach] Error getting history:', error);
        return [];
    }
}

/**
 * Clear conversation history on server
 */
export async function clearConversationHistory(): Promise<boolean> {
    const clearHistory = httpsCallable<void, { success: boolean; error?: string }>(
        functions,
        'clearMoneyCoachHistory'
    );

    try {
        const result = await clearHistory();
        return result.data.success;
    } catch (error) {
        console.error('[MoneyCoach] Error clearing history:', error);
        return false;
    }
}

export interface SpendingInsight {
    type: 'warning' | 'info' | 'success';
    title: string;
    message: string;
    icon: string;
}

/**
 * Get proactive spending suggestions/insights
 */
export async function getProactiveSuggestions(): Promise<SpendingInsight[]> {
    const getSuggestions = httpsCallable<void, { success: boolean; insights: SpendingInsight[]; error?: string }>(
        functions,
        'getProactiveSuggestions'
    );

    try {
        const result = await getSuggestions();
        if (!result.data.success) {
            console.error('[MoneyCoach] Failed to get suggestions:', result.data.error);
            return [];
        }
        return result.data.insights;
    } catch (error) {
        console.error('[MoneyCoach] Error getting suggestions:', error);
        return [];
    }
}

/**
 * Suggested prompts for new users
 */
export const SUGGESTED_PROMPTS = [
    "How much did I spend this month?",
    "What's my biggest expense category?",
    "Am I on track with my budget?",
    "How does my spending compare to last month?",
    "Where can I save money?"
];
